import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { clearAuthData } from '../utils/storage';

class ApiService {
  private api: AxiosInstance;
  private token: string | null = null;
  public baseURL: string;

  constructor() {
    this.baseURL = (process.env.REACT_APP_API_URL || (typeof window !== 'undefined' ? window.location.origin.replace(/\/$/, '') : ''));

    this.api = axios.create({
      baseURL: `${this.baseURL}/api`,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to handle headers and auth
    this.api.interceptors.request.use(
      (config) => {
        // Preserve any custom headers from the config
        const existingHeaders = (config.headers as any) || {};

        // Create default headers
        const defaultHeaders: Record<string, string> = {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        };

        // Only add auth header if we have a token
        if (this.token) {
          defaultHeaders['Authorization'] = `Bearer ${this.token}`;
        }

        // Merge: existing headers take precedence over defaults
        (config.headers as any) = { ...defaultHeaders, ...existingHeaders } as any;
        config.withCredentials = false;

        // Log the request for debugging
        console.debug('API Request:', {
          url: config.url,
          method: config.method,
          headers: config.headers,
          data: config.data
        });

        return config;
      },
      (error: AxiosError) => {
        console.error('Request interceptor error:', error);
        return Promise.reject(error);
      }
    );

    // Response interceptor to handle errors and logging
    this.api.interceptors.response.use(
      (response) => {
        console.debug('API Response:', {
          url: response.config.url,
          status: response.status,
          data: response.data
        });
        return response;
      },
      (error: AxiosError) => {
        const status = error.response?.status ?? 0;
        if (!status || status >= 500) {
          console.error('API Error:', {
            url: error.config?.url,
            method: error.config?.method,
            status: status,
            statusText: error.response?.statusText,
            headers: error.config?.headers,
            data: error.response?.data
          });
        } else if (status >= 400) {
          const msg = (error.response?.data as any)?.message;
          console.warn('API 4xx:', error.config?.url, status, msg);
        }

        if (error.response?.status === 401) {
          // Clear auth data and redirect to login
          clearAuthData();
          this.removeToken();

          const onLogin = typeof window !== 'undefined' && window.location.pathname === '/login';
          if (!onLogin) {
            window.location.href = '/login';
          }
        } else if (error.response?.status === 431) {
          // Clear all storage and reload the page
          clearAuthData();
          this.removeToken();
          window.location.reload();
        }

        // Normalize error with server message when available
        const serverData: any = error.response?.data || {};
        const msg = serverData?.message || 'An error occurred';
        const normalized = new Error(msg) as any;
        normalized.status = error.response?.status;
        normalized.data = serverData;
        return Promise.reject(normalized);
      }
    );
  }

  private shouldAdapt(url: string): boolean {
    const path = url.split('?')[0];
    // Skip adapting for known non-resource endpoints
    if (/\/(export|stats|config|features|health)(\/|$)/.test(path)) return false;
    // Adapt only for known resources that currently return { success, data, ... }
    const resources = [
      'ingredients',
      'ingredient-families',
      'diets',
      'menus',
      'recipes',
      'recipe-plates',
      'prices',
      'clients',
      'payments',
      'receipts',
      'route-deliveries',
    ];
    return resources.some((r) => path.startsWith(`/${r}`));
  }

  private mapKeys(resource: string) {
    const map: Record<string, { singular: string; plural: string }> = {
      'ingredients': { singular: 'ingredient', plural: 'ingredients' },
      'ingredient-families': { singular: 'ingredientFamily', plural: 'ingredientFamilies' },
      'diets': { singular: 'diet', plural: 'diets' },
      'menus': { singular: 'menu', plural: 'menus' },
      'recipes': { singular: 'recipe', plural: 'recipes' },
      'recipe-plates': { singular: 'recipePlate', plural: 'recipePlates' },
      'prices': { singular: 'price', plural: 'prices' },
      'clients': { singular: 'client', plural: 'clients' },
      'payments': { singular: 'payment', plural: 'payments' },
      'receipts': { singular: 'receipt', plural: 'receipts' },
      'route-deliveries': { singular: 'routeDelivery', plural: 'routeDeliveries' },
    };
    return map[resource];
  }

  private adaptResponse(url: string, payload: any): any {
    if (!payload || typeof payload !== 'object') return payload;
    if (!this.shouldAdapt(url)) return payload;

    // Only adapt if server returned the generic wrapper
    if (!('success' in payload) || !('data' in payload)) return payload;

    const path = url.split('?')[0];
    const segments = path.split('/').filter(Boolean); // ["ingredients", ":id"]
    const resource = segments[0] || '';
    const keyMap = this.mapKeys(resource);
    if (!keyMap) return payload;

    const isList = 'pagination' in payload && Array.isArray(payload.data);

    if (isList) {
      return {
        [keyMap.plural]: payload.data,
        pagination: payload.pagination,
      };
    }

    // Single item or non-paginated
    const adapted: any = { [keyMap.singular]: payload.data };
    if (typeof payload.message === 'string') adapted.message = payload.message;
    return adapted;
  }

  setToken(token: string) {
    this.token = token;
  }

  removeToken() {
    this.token = null;
  }

  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<any> = await this.api.get(url, config);
    return this.adaptResponse(url, response.data);
  }

  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<any> = await this.api.post(url, data, config);
    return this.adaptResponse(url, response.data);
  }

  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<any> = await this.api.put(url, data, config);
    return this.adaptResponse(url, response.data);
  }

  async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<any> = await this.api.patch(url, data, config);
    return this.adaptResponse(url, response.data);
  }

  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<any> = await this.api.delete(url, config);
    return this.adaptResponse(url, response.data);
  }
}

export const apiService = new ApiService();
